import { useCallback, useEffect, useMemo, useState } from "react";
import { ByoWord } from "@pages/Byo/ByoContext";

export const BYO_COLORS: string[] = [
    '#66DEB2',
    '#7B16F5',
    '#F19B38',
    '#50B1F9',
    '#D82E85',
];

export function useByoColors(data: ByoWord[], sortingKey: string) {
    const [colors, setColors] = useState<Map<string, string>>(new Map());

    const isAllLoading = useMemo(
        () => sortingKey === 'isLoading',
        [sortingKey]
    );

    useEffect(() => {
        if (isAllLoading) {
            setColors(new Map());
        }
        else {
            const firstFiveWords = data.slice(0, 5);
            const newColors = new Map<string, string>();
            firstFiveWords.forEach((word, index) => {
                if (index < BYO_COLORS.length) {
                    newColors.set(word.keyword.name ?? '', BYO_COLORS[index]);
                }
            });

            setColors(newColors);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllLoading]);

    const unsetColors = useCallback(
        (keywordNames: string[]) => {
            setColors(prev => {
                const newColors = new Map(prev);
                keywordNames.forEach(keywordName => {
                    newColors.delete(keywordName);
                });
                return newColors;
            });
        },
        []
    );

    const addColors = useCallback(
        (keywordNames: string[]) => {
            setColors(prev => {
                const newColors = new Map(prev);
                keywordNames.forEach((keywordName, index) => {
                    if (!newColors.has(keywordName)) {
                        const usedColors = new Set(newColors.values());
                        const availableColor = BYO_COLORS.find(color => !usedColors.has(color));
                        if (availableColor) {
                            newColors.set(keywordName, availableColor);
                        }
                    }
                });
                return newColors;
            });
        },
        []
    );

    useEffect(() => {
        setColors(prev => {
            const newColors = new Map(prev);

            Array.from(newColors.keys()).forEach(key => {
                if (!data.some(word => word.keyword.name === key)) {
                    newColors.delete(key);
                }
            });

            return newColors;
        })
    }, [data]);

    return {
        colors,
        addColors,
        unsetColors,
    };
}